import { FC, MouseEventHandler, ReactElement } from "react";
import { Restricted } from "../../Restricted";
import { LoadedContent } from "../../DataDisplay/LoadedContent";
import { ChevronIcon } from "../ChevronIcon";
import { AlertIcon } from "../../../ui/icons";
import {
  DD_STYLES,
  DT_STYLES,
  FIXED_HEIGHT_ROW_STYLES,
  ROW_GRID_STYLES,
} from "../../../ui/layout/detailedPages/styles";
import { Styled } from "../../../ui/layout";

export type NamedRowProps = {
  title: string | undefined;
  content?: any;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  editPermissions?: string;
  icon?: ReactElement;
  isButton?: boolean;
  subTitle?: string | string[];
  fixedHeight?: boolean;
  isLoading?: boolean;
  hideAlertIcon?: boolean;
  customId?: string;
};

const renderIcon = (icon: ReactElement | undefined) => icon ?? <ChevronIcon />;

const NAMED_ROW_STYLES =
  "w-full relative grid auto-cols-[1fr_minmax(0,20px)] grid-flow-col items-center gap-4 px-4 text-sm transition duration-300 ease-in-out ";

export const NamedRow: FC<React.PropsWithChildren<NamedRowProps & Styled>> = ({
  title,
  content,
  onClick,
  icon,
  children,
  editPermissions,
  className = "",
  isButton = false,
  fixedHeight = false,
  subTitle,
  isLoading,
  hideAlertIcon = false,
  customId,
}) => {
  const getContent = (): any => children ?? content ?? "Not defined";
  return (
    <div
      className={`${NAMED_ROW_STYLES} ${
        onClick && "cursor-pointer hover:bg-stone-100 dark:hover:bg-stone-700"
      } ${fixedHeight ? FIXED_HEIGHT_ROW_STYLES : "py-4"} ${className}`}
    >
      <dl className={ROW_GRID_STYLES}>
        <dt
          className={`${DT_STYLES} ${
            isButton ? "text-stone-700 dark:text-stone-300" : ""
          }`}
        >
          {content === null && !hideAlertIcon && (
            <AlertIcon className="relative bottom-[1.3px] mr-2.5 inline w-4" />
          )}
          <div className="min-w-0 truncate">
            {title}
            {subTitle &&
              (Array.isArray(subTitle) ? (
                (subTitle as string[]).map((item, index) => {
                  return (
                    <div
                      key={index}
                      className="text-xs font-normal text-stone-700 dark:text-stone-300 "
                    >
                      {item}
                    </div>
                  );
                })
              ) : (
                <div className="text-xs font-normal text-stone-700 dark:text-stone-300">
                  {subTitle}
                </div>
              ))}
          </div>
        </dt>
        {!isButton && (
          <dd
            className={`${DD_STYLES} ${onClick ? "mr-7" : ""}`}
            title="Row content"
          >
            <LoadedContent isLoading={!!isLoading} content={getContent()} />
          </dd>
        )}
      </dl>

      <Restricted permissions={editPermissions ?? ""}>
        {onClick !== undefined ? (
          <button
            aria-label="edit"
            onClick={onClick}
            className="absolute inset-0 h-full w-full"
            id={customId}
            disabled={isLoading}
          >
            {!isButton && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 transform">
                {renderIcon(icon)}
              </div>
            )}
          </button>
        ) : null}
      </Restricted>
    </div>
  );
};
